/**
 * Strategy Lab — scanner field & operator catalog
 * ================================================
 * The day-trading data points a scanner condition can reference, plus the
 * operator metadata the tree builder uses to decide which value input(s) to
 * render. This is the Lab's own field list (the core BotBuilder's
 * `botBuilderConstants.ENTRY_FIELDS` is entry-signal oriented and keyed to
 * `operator`, not the scanner DSL's `op`), so it lives beside the Lab UI.
 */

import type { ScannerOp } from '../../types/strategyLab';

export type ScannerFieldType = 'number' | 'string';

export interface ScannerFieldDef {
  key: string;
  label: string;
  description: string;
  category: string;
  type: ScannerFieldType;
  unit?: string;
}

/** Curated day-trading data points (~90% already sourced by the enricher stack). */
export const SCANNER_FIELDS: ScannerFieldDef[] = [
  // Price
  { key: 'price', label: 'Price', description: 'Last trade price', category: 'Price', type: 'number', unit: '$' },
  { key: 'change_percent', label: 'Change %', description: 'Intraday price change from prior close', category: 'Price', type: 'number', unit: '%' },
  { key: 'gap_percent', label: 'Gap %', description: 'Pre-market gap vs prior close', category: 'Price', type: 'number', unit: '%' },
  { key: 'day_high', label: 'Day High', description: 'Session high price', category: 'Price', type: 'number', unit: '$' },
  { key: 'day_low', label: 'Day Low', description: 'Session low price', category: 'Price', type: 'number', unit: '$' },
  { key: 'open_price', label: 'Open', description: 'Session open price', category: 'Price', type: 'number', unit: '$' },
  { key: 'prev_close', label: 'Prev Close', description: 'Previous session close', category: 'Price', type: 'number', unit: '$' },
  { key: 'atr_pct', label: 'ATR %', description: 'Average True Range as % of price', category: 'Price', type: 'number', unit: '%' },

  // Volume
  { key: 'volume', label: 'Volume', description: 'Cumulative session volume', category: 'Volume', type: 'number' },
  { key: 'relative_volume', label: 'Relative Volume', description: 'Volume vs average (2.0 = 2x normal)', category: 'Volume', type: 'number', unit: 'x' },
  { key: 'avg_volume', label: 'Avg Volume', description: 'Average daily volume', category: 'Volume', type: 'number' },
  { key: 'dollar_volume', label: 'Dollar Volume', description: 'Price x volume (liquidity)', category: 'Volume', type: 'number', unit: '$' },

  // Technical
  { key: 'rsi_14', label: 'RSI (14)', description: '14-period Relative Strength Index (0-100)', category: 'Technical', type: 'number' },
  { key: 'vwap', label: 'VWAP', description: 'Volume Weighted Average Price', category: 'Technical', type: 'number', unit: '$' },
  { key: 'price_vs_vwap', label: 'Price vs VWAP', description: 'Price % above/below VWAP', category: 'Technical', type: 'number', unit: '%' },
  { key: 'macd', label: 'MACD', description: 'MACD line', category: 'Technical', type: 'number' },
  { key: 'macd_signal', label: 'MACD Signal', description: 'MACD signal line', category: 'Technical', type: 'number' },
  { key: 'macd_histogram', label: 'MACD Histogram', description: 'MACD histogram (momentum)', category: 'Technical', type: 'number' },
  { key: 'ema_9', label: 'EMA 9', description: '9-period exponential moving average', category: 'Technical', type: 'number', unit: '$' },
  { key: 'ema_21', label: 'EMA 21', description: '21-period exponential moving average', category: 'Technical', type: 'number', unit: '$' },
  { key: 'sma_50', label: 'SMA 50', description: '50-period simple moving average', category: 'Technical', type: 'number', unit: '$' },
  { key: 'sma_200', label: 'SMA 200', description: '200-period simple moving average', category: 'Technical', type: 'number', unit: '$' },

  // Structure
  { key: 'price_vs_hod', label: 'Price vs HOD', description: 'Price relative to High of Day', category: 'Structure', type: 'number', unit: '%' },
  { key: 'price_vs_pmh', label: 'Price vs PMH', description: 'Price relative to Pre-Market High', category: 'Structure', type: 'number', unit: '%' },
  { key: 'float_rotation', label: 'Float Rotation', description: 'Session volume / float shares', category: 'Structure', type: 'number', unit: 'x' },

  // Fundamentals
  { key: 'market_cap', label: 'Market Cap', description: 'Market capitalization (USD)', category: 'Fundamentals', type: 'number', unit: '$' },
  { key: 'float_shares', label: 'Float', description: 'Float shares outstanding', category: 'Fundamentals', type: 'number' },
  { key: 'shares_outstanding', label: 'Shares Outstanding', description: 'Total shares outstanding', category: 'Fundamentals', type: 'number' },
  { key: 'short_interest_pct', label: 'Short Interest %', description: 'Short interest as % of float', category: 'Fundamentals', type: 'number', unit: '%' },
  { key: 'beta', label: 'Beta', description: 'Stock beta (1.0 = market average)', category: 'Fundamentals', type: 'number' },

  // Catalyst / metadata (string fields — use string operators)
  { key: 'sector', label: 'Sector', description: 'GICS sector name', category: 'Catalyst', type: 'string' },
  { key: 'industry', label: 'Industry', description: 'Industry classification', category: 'Catalyst', type: 'string' },
  { key: 'exchange', label: 'Exchange', description: 'Listing exchange (NYSE, NASDAQ, ...)', category: 'Catalyst', type: 'string' },
  { key: 'has_news_catalyst', label: 'News Catalyst', description: 'Recent news-catalyst flag (true/false)', category: 'Catalyst', type: 'string' },
];

export const SCANNER_FIELD_MAP: Record<string, ScannerFieldDef> = SCANNER_FIELDS.reduce(
  (acc, f) => {
    acc[f.key] = f;
    return acc;
  },
  {} as Record<string, ScannerFieldDef>,
);

/** How a given operator renders its value input(s). */
export type ScannerOpKind = 'numeric' | 'range' | 'cross' | 'string';

export interface ScannerOpDef {
  value: ScannerOp;
  label: string;
  kind: ScannerOpKind;
}

export const SCANNER_OPERATORS: ScannerOpDef[] = [
  { value: 'gt', label: '>', kind: 'numeric' },
  { value: 'gte', label: '>=', kind: 'numeric' },
  { value: 'lt', label: '<', kind: 'numeric' },
  { value: 'lte', label: '<=', kind: 'numeric' },
  { value: 'eq', label: '=', kind: 'numeric' },
  { value: 'neq', label: '!=', kind: 'numeric' },
  { value: 'between', label: 'between', kind: 'range' },
  { value: 'cross_above', label: 'crosses above', kind: 'cross' },
  { value: 'cross_below', label: 'crosses below', kind: 'cross' },
  { value: 'eq_str', label: 'equals (text)', kind: 'string' },
  { value: 'contains', label: 'contains', kind: 'string' },
  { value: 'in_list', label: 'in list', kind: 'string' },
];

export const SCANNER_OP_MAP: Record<ScannerOp, ScannerOpDef> = SCANNER_OPERATORS.reduce(
  (acc, op) => {
    acc[op.value] = op;
    return acc;
  },
  {} as Record<ScannerOp, ScannerOpDef>,
);

/** Operators valid for a field type. String fields only get string operators, and vice-versa. */
export function operatorsForFieldType(type: ScannerFieldType): ScannerOpDef[] {
  if (type === 'string') return SCANNER_OPERATORS.filter((o) => o.kind === 'string');
  return SCANNER_OPERATORS.filter((o) => o.kind !== 'string');
}

/** Default operator when a field of the given type is first selected. */
export function defaultOpForFieldType(type: ScannerFieldType): ScannerOp {
  return type === 'string' ? 'eq_str' : 'gt';
}
